/**
 * Google Analytics 4 helper functions for Haven Account app
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

type AuthMethod = 'google' | 'microsoft' | 'apple' | 'email';
type Plan = 'free' | 'premium';
type BillingCycle = 'monthly' | 'yearly';

interface UserProperties {
  user_tier?: Plan;
  user_ltv?: number;
  signup_date?: string;
  billing_cycle?: BillingCycle;
  has_extension?: boolean;
  has_desktop_app?: boolean;
}

/**
 * Wait for gtag to be available, then execute a callback.
 * Retries every 100ms for up to 5 seconds.
 */
function withGtag(fn: (gtag: NonNullable<Window['gtag']>) => void): void {
  if (typeof window.gtag === 'function') {
    fn(window.gtag);
    return;
  }
  let attempts = 0;
  const interval = setInterval(() => {
    attempts++;
    if (typeof window.gtag === 'function') {
      clearInterval(interval);
      fn(window.gtag);
    } else if (attempts >= 50) {
      clearInterval(interval);
      console.warn('[Analytics] gtag not available after 5s, dropping event');
    }
  }, 100);
}

// Anyone can write `source` into a link, so only a slug goes into an event param or the backend. The raw URL still
// reaches GA4 in page_location, since index.html's PII strip doesn't cover `source`.
export function cleanSignupSource(raw: string | null | undefined): string | null {
  if (!raw || raw.includes('@')) return null;
  const cleaned = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
  return cleaned || null;
}

export function signupSource(): string | null {
  return cleanSignupSource(new URLSearchParams(window.location.search).get('source'));
}

/**
 * Read UTM params from the current URL. Returns empty strings when absent.
 * `?source=` stands in for a missing `utm_source` (ENG-3601).
 */
function getUrlAttribution(): { utm_source: string; utm_medium: string; utm_campaign: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || signupSource() || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
  };
}

/**
 * Track user signup event.
 *
 * ENG-3443: the sign-in provider is all this event says about the person. It used to carry their email
 * address as `user_email`, which put personal data in GA4 (and in the dataLayer that GTM-PGVN9588
 * reads) against Google's terms. No parameter of any GA4 event here may be an email, a uid, a tenant
 * id or a token; `analytics.test.ts` holds every event in this file to that.
 */
export function trackSignup(method: AuthMethod) {
  const attribution = getUrlAttribution();
  withGtag((gtag) => {
    gtag('event', 'sign_up', {
      method,
      ...(attribution.utm_source && { traffic_source: attribution.utm_source }),
      ...(attribution.utm_medium && { traffic_medium: attribution.utm_medium }),
      ...(attribution.utm_campaign && { traffic_campaign: attribution.utm_campaign }),
    });
    console.log('[Analytics] Tracked sign_up:', { method, attribution });
  });
}

export type SignupFunnelPlan = 'individual' | 'family' | 'business';
export type PlanSelectedFrom = 'plan_step' | 'url';

// ENG-3601: the self-serve signup funnel.

function trackSignupFunnel(name: string, params: { plan?: SignupFunnelPlan; selected_from?: PlanSelectedFrom }) {
  const source = signupSource();
  withGtag((gtag) => {
    gtag('event', name, { ...params, ...(source && { source }) });
  });
}

export function trackSignupPlanStepViewed() {
  trackSignupFunnel('signup_plan_step_viewed', {});
}

export function trackSignupPlanSelected(plan: SignupFunnelPlan, selectedFrom: PlanSelectedFrom) {
  trackSignupFunnel('signup_plan_selected', { plan, selected_from: selectedFrom });
}

export function trackOrgCreated(plan: Exclude<SignupFunnelPlan, 'individual'>) {
  trackSignupFunnel('org_created', { plan });
}

export function trackIndividualToWebStore() {
  trackSignupFunnel('individual_to_web_store', {});
}

/**
 * Track user login event
 */
export function trackLogin(method: AuthMethod, isDesktopApp: boolean = false) {
  withGtag((gtag) => {
    gtag('event', 'login', {
      method,
      platform: isDesktopApp ? 'desktop_app' : 'web',
    });
    console.log('[Analytics] Tracked login:', { method, platform: isDesktopApp ? 'desktop_app' : 'web' });
  });
}

/**
 * Track desktop app login specifically
 */
export function trackDesktopAppLogin(plan: Plan) {
  withGtag((gtag) => {
    gtag('event', 'desktop_app_login', {
      user_tier: plan,
    });
    console.log('[Analytics] Tracked desktop_app_login:', { user_tier: plan });
  });
}

/**
 * Track subscription purchase
 */
export function trackPurchase(plan: Plan, value: number, billingCycle: BillingCycle, seats: number = 1) {
  withGtag((gtag) => {
    const itemName = 'Haven Premium';

    gtag('event', 'purchase', {
      transaction_id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      value,
      currency: 'USD',
      items: [{
        item_id: plan,
        item_name: itemName,
        price: value,
        quantity: seats,
        item_category: 'Subscription',
        item_variant: billingCycle,
      }],
      billing_cycle: billingCycle,
      seats,
    });
    console.log('[Analytics] Tracked purchase:', { plan, value, billingCycle, seats });
  });
}

/**
 * Track subscription cancellation
 */
export function trackSubscriptionCancelled(plan: Plan, reason?: string) {
  withGtag((gtag) => {
    gtag('event', 'subscription_cancelled', {
      plan,
      cancellation_reason: reason || 'not_provided',
    });
    console.log('[Analytics] Tracked subscription_cancelled:', { plan, reason });
  });
}

/**
 * Track subscription upgrade
 */
export function trackSubscriptionUpgrade(fromPlan: Plan, toPlan: Plan) {
  withGtag((gtag) => {
    gtag('event', 'subscription_upgraded', {
      from_plan: fromPlan,
      to_plan: toPlan,
    });
    console.log('[Analytics] Tracked subscription_upgraded:', { fromPlan, toPlan });
  });
}

/**
 * Set user properties in GA4
 */
export function setUserProperties(properties: UserProperties) {
  withGtag((gtag) => {
    gtag('set', 'user_properties', properties);
    console.log('[Analytics] Set user properties:', properties);
  });
}

/**
 * Calculate annual LTV based on plan
 */
export function calculateAnnualLTV(plan: Plan, seats: number = 1, isYearly: boolean = true): number {
  switch (plan) {
    case 'premium':
      // $6/user/month = $60/user/year (yearly is discounted to $5/month)
      return isYearly ? 60 * seats : 6 * seats * 12;
    case 'free':
    default:
      return 0;
  }
}

/**
 * Track extension install confirmed (fired once when user lands on the post-install page).
 * Reads UTM params that the extension appended to the URL so GA4 can attribute the install
 * back to the original traffic source.
 */
export function trackExtensionPostInstallPageViewed() {
  const attribution = getUrlAttribution();
  withGtag((gtag) => {
    gtag('event', 'extension_installed', {
      ...(attribution.utm_source && { traffic_source: attribution.utm_source }),
      ...(attribution.utm_medium && { traffic_medium: attribution.utm_medium }),
      ...(attribution.utm_campaign && { traffic_campaign: attribution.utm_campaign }),
    });
    gtag('event', 'extension_post_install_page_viewed', {
      ...(attribution.utm_source && { traffic_source: attribution.utm_source }),
    });
    console.log('[Analytics] Tracked extension_installed + extension_post_install_page_viewed:', attribution);
  });
}

/**
 * Track page view with custom parameters
 */
export function trackPageView(pageName: string, additionalParams?: Record<string, any>) {
  withGtag((gtag) => {
    gtag('event', 'page_view', {
      page_title: pageName,
      ...additionalParams,
    });
    console.log('[Analytics] Tracked page_view:', { pageName, ...additionalParams });
  });
}
